import React, { useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { message } from "antd";
import axios from "axios";
import "./AdminOrder.css";

const PAGE_SIZE = 20;

const AdminUsers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // server pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // ✅ server filters
  const [searchText, setSearchText] = useState(""); // email OR orderId
  const [selectedStatus, setSelectedStatus] = useState(""); // success/pending/failed...

  // optional client-only filters (these do NOT hit backend)
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // debounce timer
  const tRef = useRef(null);

  // per-row updating state
  const [updatingId, setUpdatingId] = useState("");

  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    }),
    []
  );

  const buildUrl = ({ skip = 0 } = {}) => {
    const q = new URLSearchParams();

    // ✅ if status selected: always skip=0 and limit=100
    const useSkip = selectedStatus ? 0 : skip;
    const useLimit = selectedStatus ? 100 : PAGE_SIZE;

    q.set("skip", String(useSkip));
    q.set("limit", String(useLimit));

    if (searchText.trim()) q.set("search", searchText.trim());
    if (selectedStatus) q.set("status", selectedStatus);

    return `/api/admin/get-all-orders?${q.toString()}`;
  };

  const fetchOrders = async ({ reset = false } = {}) => {
    // ✅ if status selected, always behave like reset (no append)
    const forceReset = selectedStatus ? true : reset;

    try {
      if (forceReset) setLoading(true);
      else setLoadingMore(true);

      const skip = forceReset ? 0 : rows.length;

      const res = await axios.get(buildUrl({ skip }), authHeaders);

      if (!res.data?.success) {
        message.error(res.data?.message || "No orders found");
        if (forceReset) {
          setRows([]);
          setTotalCount(0);
          setHasMore(false);
        }
        return;
      }

      const incoming = Array.isArray(res.data?.data) ? res.data.data : [];
      const nextTotal = Number(res.data?.totalCount) || 0;

      setTotalCount(nextTotal);

      // ✅ status selected => show last 100 and disable load more
      if (selectedStatus) {
        setRows(incoming);
        setHasMore(false);
        return;
      }

      const nextHasMore = !!res.data?.hasMore;
      setHasMore(nextHasMore);

      if (forceReset) {
        setRows(incoming);
      } else {
        // append + dedupe by _id/orderId
        const merged = [...rows, ...incoming];
        const seen = new Set();
        const deduped = [];
        for (const o of merged) {
          const key = String(o?._id || o?.orderId || "");
          if (!key || seen.has(key)) continue;
          seen.add(key);
          deduped.push(o);
        }
        setRows(deduped);
      }
    } catch (e) {
      console.log(e);
      message.error("Failed to fetch orders");
      if (forceReset) {
        setRows([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // ✅ Debounced search typing (server-side)
  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      fetchOrders({ reset: true });
    }, 400);

    return () => {
      if (tRef.current) clearTimeout(tRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // ✅ status change -> immediate fetch last 100
  useEffect(() => {
    fetchOrders({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  // initial load
  useEffect(() => {
    fetchOrders({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const safeDateTime = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleString("default", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const orderType = (o) =>
    String(o?.api || "").toLowerCase() === "no" ? "Manual" : "API";

  const statusClass = (st) => {
    const s = String(st || "").toLowerCase();
    if (s === "success") return "st success";
    if (s === "pending") return "st pending";
    if (s === "failed") return "st failed";
    if (s === "issue") return "st issue";
    if (s === "processing") return "st processing";
    if (s === "cancel") return "st cancel";
    return "st other";
  };

  // ✅ Update order status
  const updateOrderStatus = async (orderMongoId, newStatus) => {
    const id = String(orderMongoId || "");
    const status = String(newStatus || "").trim().toLowerCase();
    if (!id) return;

    // optimistic UI
    const prev = rows;
    setRows((p) => p.map((x) => (String(x?._id) === id ? { ...x, status } : x)));

    try {
      setUpdatingId(id);
      const res = await axios.post(
        "/api/admin/update-order",
        { id, status },
        authHeaders
      );

      if (!res.data?.success) {
        message.error(res.data?.message || "Failed to update");
        setRows(prev);
        return;
      }

      message.success("Order status updated");

      // ✅ if currently filtered by a status and admin changes it,
      // order might no longer belong in the list -> refetch
      if (selectedStatus) {
        fetchOrders({ reset: true });
      }
    } catch (e) {
      message.error("Failed to update");
      setRows(prev);
    } finally {
      setUpdatingId("");
    }
  };

  // ✅ Optional: client-side date/month filter only
  const visibleOrders = useMemo(() => {
    const src = Array.isArray(rows) ? rows : [];

    return src.filter((o) => {
      const created = o?.createdAt ? new Date(o.createdAt) : null;

      const dateMatch =
        !selectedDate
          ? true
          : created
          ? created.toISOString().split("T")[0] ===
            new Date(selectedDate).toISOString().split("T")[0]
          : false;

      const monthMatch =
        !selectedMonth
          ? true
          : created
          ? created.getMonth() === Number(selectedMonth) - 1
          : false;

      return dateMatch && monthMatch;
    });
  }, [rows, selectedDate, selectedMonth]);

  const handleClearFilter = () => {
    setSearchText("");
    setSelectedStatus("");
    setSelectedDate("");
    setSelectedMonth("");
    // fetch triggers automatically due to effects
  };

  return (
    <AdminLayout>
      <div className="admin-users-container">
        <div className="page-title admin-order-title">
          <div>
            <h3 className="m-0">Orders</h3>
            <div className="admin-order-sub">
              Total (server): <b>{totalCount || 0}</b>
              <span className="dot" />
              Loaded: <b>{rows?.length || 0}</b>
              <span className="dot" />
              Showing: <b>{visibleOrders?.length || 0}</b>
              {selectedStatus && (
                <>
                  <span className="dot" />
                  Mode: <b>Last 100 “{selectedStatus}”</b>
                </>
              )}
            </div>
          </div>

          <button className="admin-order-refresh" onClick={() => fetchOrders({ reset: true })}>
            Refresh
          </button>
        </div>

        <hr />

        <div className="admin-order-wrap">
          {/* Filters */}
          <div className="admin-order-filters">
            <div className="field">
              <label>Search</label>
              <input
                type="search"
                placeholder="Search by Email or OrderId"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div className="field">
              <label>Status</label>
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="issue">Issue</option>
                <option value="processing">Processing</option>
                <option value="cancel">Cancel</option>
              </select>
            </div>

            <div className="field">
              <label>Date</label>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            </div>

            <div className="field">
              <label>Month</label>
              <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                <option value="">All Months</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>

            <div className="field actions">
              <button className="btn-clear" onClick={handleClearFilter}>
                Clear Filter
              </button>
            </div>
          </div>

          {/* Loading */}
          {loading ? (
            <div className="admin-order-loading">
              <div className="spinner" />
              <div>Loading orders…</div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="admin-order-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Order Id</th>
                      <th>Type</th>
                      <th>Product</th>
                      <th>Product Name</th>
                      <th>UserID</th>
                      <th>ZoneID</th>
                      <th>Email</th>
                      <th>Amount</th>
                      <th>Status (Edit)</th>
                      <th>Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleOrders?.map((o, index) => (
                      <tr key={o?._id || o?.orderId || index}>
                        <td className="mono">{o?.orderId || "-"}</td>
                        <td>
                          <span className="pill">{orderType(o)}</span>
                        </td>
                        <td className="truncate">{o?.productinfo || "-"}</td>
                        <td className="truncate">{o?.orderDetails || "-"}</td>
                        <td className="mono">{o?.userId || "-"}</td>
                        <td className="mono">{o?.zoneId || "-"}</td>
                        <td className="truncate">{o?.email || "-"}</td>
                        <td className="mono">₹{o?.amount || "-"}</td>

                        <td>
                          <div className="stWrap">
                            <span className={statusClass(o?.status)}>{String(o?.status || "-")}</span>
                            <select
                              className="stSelect"
                              value={String(o?.status || "").toLowerCase()}
                              disabled={updatingId === String(o?._id)}
                              onChange={(e) => updateOrderStatus(o?._id, e.target.value)}
                            >
                              <option value="success">Success</option>
                              <option value="pending">Pending</option>
                              <option value="failed">Failed</option>
                              <option value="issue">Issue</option>
                              <option value="processing">Processing</option>
                              <option value="cancel">Cancel</option>
                            </select>
                          </div>
                        </td>

                        <td className="mono">{safeDateTime(o?.createdAt)}</td>
                      </tr>
                    ))}

                    {!visibleOrders?.length && (
                      <tr>
                        <td colSpan="10" className="text-center">
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="admin-order-cards">
                {visibleOrders?.map((o, index) => (
                  <div className="order-card" key={o?._id || o?.orderId || index}>
                    <div className="top">
                      <div className="left">
                        <div className="oid mono">{o?.orderId || "-"}</div>
                        <div className="sub">
                          <span className="pill">{orderType(o)}</span>
                          <span className={statusClass(o?.status)}>{String(o?.status || "-")}</span>
                        </div>
                      </div>
                      <div className="right mono">₹{o?.amount || "-"}</div>
                    </div>

                    <div className="mid">
                      <div className="label">Product</div>
                      <div className="value">{o?.productinfo || "-"}</div>
                    </div>

                    <div className="mid">
                      <div className="label">Product Name</div>
                      <div className="value">{o?.orderDetails || "-"}</div>
                    </div>

                    <div className="mid">
                      <div className="label">Email</div>
                      <div className="value">{o?.email || "-"}</div>
                    </div>

                    <div className="mid">
                      <div className="label">UserID</div>
                      <div className="value">{o?.userId || "-"}</div>
                    </div>

                    <div className="mid">
                      <div className="label">ZoneID</div>
                      <div className="value">{o?.zoneId || "-"}</div>
                    </div>

                    <div className="mid">
                      <div className="label">Update Status</div>
                      <select
                        className="stSelectMobile"
                        value={String(o?.status || "").toLowerCase()}
                        disabled={updatingId === String(o?._id)}
                        onChange={(e) => updateOrderStatus(o?._id, e.target.value)}
                      >
                        <option value="success">Success</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="issue">Issue</option>
                        <option value="processing">Processing</option>
                        <option value="cancel">Cancel</option>
                      </select>
                    </div>

                    <div className="footer mono">{safeDateTime(o?.createdAt)}</div>
                  </div>
                ))}

                {!visibleOrders?.length && <div className="empty">No orders found</div>}
              </div>

              {/* Load more */}
              <div className="admin-order-loadmore">
                {selectedStatus ? (
                  <div className="end">Showing latest 100 “{selectedStatus}” orders.</div>
                ) : hasMore ? (
                  <button
                    className="btn-load"
                    onClick={() => fetchOrders({ reset: false })}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading..." : `Load next ${PAGE_SIZE}`}
                  </button>
                ) : (
                  <div className="end">You’re seeing all matching orders.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
